import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ApproachSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      title: 'Our Approach',
      subtitle: 'How We Deliver Excellence in Legal Services',
      description: 'Our systematic approach ensures that every client receives personalized attention and strategic legal solutions tailored to their unique circumstances.',
      steps: [
        {
          number: '01',
          icon: 'MessageSquare',
          title: 'Initial Consultation',
          description: 'We begin with a comprehensive consultation to understand your legal needs, objectives, and concerns. This allows us to provide accurate advice and develop a strategic approach.',
          features: [
            'Free initial consultation',
            'Confidential discussion',
            'Case assessment',
            'Strategy development'
          ]
        },
        {
          number: '02',
          icon: 'FileText',
          title: 'Case Analysis & Planning',
          description: 'Our team conducts thorough research and analysis of your case, reviewing all relevant documents, laws, and precedents to build a strong foundation for your matter.',
          features: [
            'Comprehensive legal research',
            'Document review',
            'Risk assessment',
            'Timeline development'
          ]
        },
        {
          number: '03',
          icon: 'Users',
          title: 'Collaborative Execution',
          description: 'We work closely with you throughout the process, keeping you informed at every step and ensuring your input is valued in all major decisions.',
          features: [
            'Regular updates',
            'Client collaboration',
            'Transparent communication',
            'Flexible scheduling'
          ]
        },
        {
          number: '04',
          icon: 'CheckCircle',
          title: 'Results & Follow-up',
          description: 'We deliver results that exceed expectations and provide ongoing support to ensure long-term success and compliance with all legal requirements.',
          features: [
            'Successful outcomes',
            'Post-case support',
            'Compliance monitoring',
            'Relationship building'
          ]
        }
      ],
      methodology: {
        title: 'Our Methodology',
        principles: [
          {
            icon: 'Search',
            title: 'Thorough Investigation',
            description: 'We leave no stone unturned in investigating every aspect of your case.'
          },
          {
            icon: 'Brain',
            title: 'Strategic Thinking',
            description: 'We apply strategic thinking to develop innovative solutions for complex legal challenges.'
          },
          {
            icon: 'Shield',
            title: 'Risk Management',
            description: 'We identify and mitigate potential risks to protect your interests.'
          },
          {
            icon: 'Clock',
            title: 'Timely Delivery',
            description: 'We respect deadlines and deliver results within agreed timeframes.'
          }
        ]
      }
    },
    sw: {
      title: 'Mbinu Yetu',
      subtitle: 'Jinsi Tunavyotoa Ubora katika Huduma za Kisheria',
      description: 'Mbinu yetu ya kimfumo inahakikisha kuwa kila mteja anapokea uangalifu wa kibinafsi na suluhisho za kisheria za kimkakati zilizobinafsishwa kulingana na hali zao za kipekee.',
      steps: [
        {
          number: '01',
          icon: 'MessageSquare',
          title: 'Mazungumzo ya Awali',
          description: 'Tunaanza na mazungumzo makuu ya kuelewa mahitaji yako ya kisheria, malengo, na wasiwasi. Hii inaturuhusu kutoa ushauri sahihi na kuunda mbinu ya kimkakati.',
          features: [
            'Mazungumzo ya kwanza ya bure',
            'Mjadala wa siri',
            'Tathmini ya kesi',
            'Maendeleo ya mkakati'
          ]
        },
        {
          number: '02',
          icon: 'FileText',
          title: 'Uchambuzi wa Kesi na Mipango',
          description: 'Timu yetu inafanya utafiti na uchambuzi wa kina wa kesi yako, ikikagua nyaraka zote muhimu, sheria, na mifano ya kujenga msingi imara wa suala lako.',
          features: [
            'Utafiti wa kina wa kisheria',
            'Ukaguzi wa nyaraka',
            'Tathmini ya hatari',
            'Maendeleo ya ratiba'
          ]
        },
        {
          number: '03',
          icon: 'Users',
          title: 'Utekelezaji wa Ushirikiano',
          description: 'Tunafanya kazi kwa karibu nawe katika mchakato wote, tukikufahamisha katika kila hatua na kuhakikisha mchango wako unathaminiwa katika maamuzi yote makuu.',
          features: [
            'Masasisho ya kawaida',
            'Ushirikiano wa mteja',
            'Mawasiliano ya uwazi',
            'Ratiba ya kubadilika'
          ]
        },
        {
          number: '04',
          icon: 'CheckCircle',
          title: 'Matokeo na Ufuatiliaji',
          description: 'Tunatoa matokeo yanayozidi matarajio na kutoa msaada wa kuendelea kuhakikisha mafanikio ya muda mrefu na kufuata mahitaji yote ya kisheria.',
          features: [
            'Matokeo ya mafanikio',
            'Msaada wa baada ya kesi',
            'Ufuatiliaji wa kufuata',
            'Kujenga mahusiano'
          ]
        }
      ],
      methodology: {
        title: 'Mbinu Yetu',
        principles: [
          {
            icon: 'Search',
            title: 'Uchunguzi wa Kina',
            description: 'Hatuachi jiwe lolote bila kuligeuza katika kuchunguza kila kipengele cha kesi yako.'
          },
          {
            icon: 'Brain',
            title: 'Mawazo ya Kimkakati',
            description: 'Tunatumia mawazo ya kimkakati kuunda suluhisho za kibunifu kwa changamoto ngumu za kisheria.'
          },
          {
            icon: 'Shield',
            title: 'Usimamizi wa Hatari',
            description: 'Tunabainisha na kupunguza hatari zinazoweza kutokea ili kulinda maslahi yako.'
          },
          {
            icon: 'Clock',
            title: 'Utoaji wa Wakati',
            description: 'Tunaheshimu tarehe za mwisho na kutoa matokeo ndani ya muda uliokubaliwa.'
          }
        ]
      }
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-surface/5 to-primary/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary">
            {currentContent.title}
          </h2>
          <h3 className="text-xl lg:text-2xl font-heading font-semibold text-primary">
            {currentContent.subtitle}
          </h3>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentContent.description}
          </p>
        </div>

        {/* Process Steps */}
        <div className="space-y-12 mb-20">
          {currentContent.steps.map((step, index) => (
            <div key={index} className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
              index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
            }`}>
              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="flex items-center space-x-4">
                  <div className="text-4xl font-heading font-bold text-primary/30">
                    {step.number}
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <Icon name={step.icon} size={24} color="white" />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-2xl font-heading font-bold text-text-primary">
                    {step.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {step.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {step.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <Icon name="Check" size={16} className="text-primary flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visual */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                <div className="card p-8 bg-white">
                  <div className="w-full h-48 bg-gradient-to-br from-surface/20 to-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={step.icon} size={64} className="text-primary/40" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Methodology */}
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary">
              {currentContent.methodology.title}
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              {currentLanguage === 'en' ?'Our proven methodology ensures consistent, high-quality results across all practice areas.' :'Mbinu yetu iliyothibitishwa inahakikisha matokeo ya ubora wa juu yanayoendelea katika maeneo yote ya utendaji.'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {currentContent.methodology.principles.map((principle, index) => (
              <div key={index} className="card p-6 text-center space-y-4 hover:shadow-lg transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto">
                  <Icon name={principle.icon} size={28} color="white" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary">
                  {principle.title}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;